import React, { useMemo, useState } from "react"
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts"

/**
 * AdminDashboard (Tabs)
 * - Analytics tab: charts + KPI cards
 * - Users tab: searchable/sortable table
 *
 * Notes:
 * - DO NOT display or log passwords. If your API returns `password`, remove it server-side.
 * - This file assumes your existing /users endpoint returns an array of users.
 */

// -----------------------------
// Analytics helpers
// -----------------------------
const YEAR_BUCKETS = {
  HS_JUNIOR: "High School Junior",
  HS_SENIOR: "High School Senior",
  COLLEGE_FRESHMAN: "College Freshman",
  COLLEGE_SOPHOMORE: "College Sophomore",
  COLLEGE_JUNIOR: "College Junior",
  COLLEGE_SENIOR: "College Senior",
  OTHER: "Other / Unknown",
}

const normalizeYear = (year) => {
  if (!year) return YEAR_BUCKETS.OTHER
  const y = String(year).toLowerCase()
  if (y.includes("high") && y.includes("junior")) return YEAR_BUCKETS.HS_JUNIOR
  if (y.includes("high") && y.includes("senior")) return YEAR_BUCKETS.HS_SENIOR
  if (y.includes("freshman")) return YEAR_BUCKETS.COLLEGE_FRESHMAN
  if (y.includes("sophomore")) return YEAR_BUCKETS.COLLEGE_SOPHOMORE
  if (y.includes("junior")) return YEAR_BUCKETS.COLLEGE_JUNIOR
  if (y.includes("senior")) return YEAR_BUCKETS.COLLEGE_SENIOR
  return YEAR_BUCKETS.OTHER
}

const normalizeLocation = (user) => {
  const country = user?.country?.trim()
  const city = user?.city?.trim()
  const state = user?.state?.trim()

  if (country && country.toLowerCase() !== "united states") return country
  if (city && state) return `${city}, ${state}`
  if (state) return state
  if (country) return country
  return "Unknown"
}

const parseGpa = (v) => {
  if (v == null) return null
  const n = Number(String(v).replace(/[^\d.]/g, ""))
  return Number.isFinite(n) ? n : null
}

const bucketGpa = (gpa) => {
  if (gpa == null) return "Unknown"
  if (gpa < 2.5) return "<2.5"
  if (gpa < 3.0) return "2.5–3.0"
  if (gpa < 3.5) return "3.0–3.5"
  if (gpa <= 4.0) return "3.5–4.0"
  return "4.0+"
}

const bucketProgress = (p) => {
  if (!Number.isFinite(p)) return "Unknown"
  if (p <= 20) return "0–20"
  if (p <= 40) return "21–40"
  if (p <= 60) return "41–60"
  if (p <= 80) return "61–80"
  if (p < 100) return "81–99"
  return "100"
}

const formatDateKey = (isoString) => {
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) return "Invalid"
  // YYYY-MM-DD in local time
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const aggregateCounts = (items, getKey) => {
  const map = new Map()
  for (const it of items) {
    const key = getKey(it)
    map.set(key, (map.get(key) || 0) + 1)
  }
  return Array.from(map.entries()).map(([name, value]) => ({ name, value }))
}

const sortDesc = (arr) => [...arr].sort((a, b) => b.value - a.value)

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n))

// -----------------------------
// Existing helpers (improved)
// -----------------------------
const calculateAccountProgress = (user) => {
  const fields = [
    "firstName",
    "lastName",
    "email",
    "ethnicBackground",
    "extracurricularActivities",
    "phoneNumber",
    "majorsConsidering",
    "disability",
    "awardsAndHonors",
    "letterOfRecommendation",
    "workExperience",
    "schoolType",
    "birthday",
    "city",
    "householdIncome",
    "state",
    "postal",
    "country",
    "schoolName",
    "yearOfStudent",
    "commonAppPersonalStatement",
    "academicWeightedGPA",
    "academicUnweightedGPA",
  ]

  const filled = fields.filter((field) => {
    const v = user?.[field]
    if (Array.isArray(v)) return v.length > 0
    return v != null && String(v).trim() !== ""
  })

  return Math.round((filled.length / fields.length) * 100)
}

const formatUserOrigin = (userOrigin) => {
  if (!userOrigin || userOrigin.length === 0 || !userOrigin[0]) return "N/A"
  const { browser, os, platform } = userOrigin[0] || {}
  if (!browser && !os && !platform) return "No data"
  const parts = []
  if (browser) parts.push(String(browser))
  if (os) parts.push(String(os))
  if (platform) parts.push(String(platform))
  return parts.join(" ")
}

const normalizeOriginBucket = (originRaw) => {
  const s = String(originRaw || "").toLowerCase()

  if (s === "scholarhive unknown unknown") return "iOS App Store"
  if (s.includes("ios")) return "iOS (browser)"
  if (s.includes("android")) return "Android"
  if (s.includes("mac")) return "Mac"
  if (s.includes("windows")) return "Windows"
  if (s.includes("linux")) return "Linux"
  if (s.includes("chrome")) return "Chrome"
  if (s.includes("safari")) return "Safari"
  if (s.includes("edge")) return "Edge"

  return originRaw || "Unknown"
}

// Credits: you currently store negative counters (e.g., -10000 baseline)
// Interpreting "credits used" as (10000 + value), clamped >= 0.
const normalizeCreditsUsed = (raw) => {
  const n = Number(raw) || 0
  return Math.max(0, 10000 + n)
}

// -----------------------------
// UI components
// -----------------------------
const Card = ({ title, subtitle, children }) => (
  <div style={styles.card}>
    <div style={{ marginBottom: 10 }}>
      <div style={styles.cardTitle}>{title}</div>
      {subtitle ? <div style={styles.cardSubtitle}>{subtitle}</div> : null}
    </div>
    {children}
  </div>
)

const StatCard = ({ label, value, hint }) => (
  <div style={styles.statCard}>
    <div style={styles.statLabel}>{label}</div>
    <div style={styles.statValue}>{value}</div>
    {hint ? <div style={styles.statHint}>{hint}</div> : null}
  </div>
)

const Pill = ({ text, tone = "neutral" }) => (
  <span
    style={{
      ...styles.pill,
      ...(tone === "green"
        ? styles.pillGreen
        : tone === "red"
        ? styles.pillRed
        : tone === "blue"
        ? styles.pillBlue
        : styles.pillNeutral),
    }}
  >
    {text}
  </span>
)

const ProgressBar = ({ value }) => {
  const v = clamp(Number(value) || 0, 0, 100)
  return (
    <div style={styles.progressOuter}>
      <div style={{ ...styles.progressInner, width: `${v}%` }} />
      <div style={styles.progressText}>{v}%</div>
    </div>
  )
}

const Tabs = ({ active, onChange }) => (
  <div style={styles.tabs}>
    <button
      onClick={() => onChange("analytics")}
      style={{ ...styles.tabBtn, ...(active === "analytics" ? styles.tabBtnActive : {}) }}
    >
      Analytics
    </button>
    <button
      onClick={() => onChange("users")}
      style={{ ...styles.tabBtn, ...(active === "users" ? styles.tabBtnActive : {}) }}
    >
      Users
    </button>
  </div>
)

const ChartContainer = ({ children }) => (
  <div style={{ width: "100%", height: 320 }}>{children}</div>
)

// -----------------------------
// Users table
// -----------------------------
const UsersTable = ({ users }) => {
  const [query, setQuery] = useState("")
  const [paidOnly, setPaidOnly] = useState(false)
  const [originFilter, setOriginFilter] = useState("All")
  const [sortKey, setSortKey] = useState("createdAt")
  const [sortDir, setSortDir] = useState("desc")

  const originOptions = useMemo(() => {
    const set = new Set()
    for (const u of users) {
      const raw = formatUserOrigin(u.userOrigin)
      set.add(normalizeOriginBucket(raw))
    }
    return ["All", ...Array.from(set).sort()]
  }, [users])

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()

    const filtered = users.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.trim().toLowerCase()
      const email = String(u.email || "").toLowerCase()
      const year = String(u.yearOfStudent || "").toLowerCase()
      const school = String(u.schoolName || "").toLowerCase()

      const rawOrigin = formatUserOrigin(u.userOrigin)
      const origin = normalizeOriginBucket(rawOrigin)

      const matchesQuery =
        !q || name.includes(q) || email.includes(q) || year.includes(q) || school.includes(q)

      const matchesPaid = !paidOnly || u.statusEnum !== 0
      const matchesOrigin = originFilter === "All" || origin === originFilter

      return matchesQuery && matchesPaid && matchesOrigin
    })

    const mapped = filtered.map((u) => {
      const createdAt = u?.createdAt ? new Date(u.createdAt) : null
      const createdAtMs = createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.getTime() : 0

      const accountProgress = clamp(calculateAccountProgress(u), 0, 100)
      const rawOrigin = formatUserOrigin(u.userOrigin)
      const origin = normalizeOriginBucket(rawOrigin)

      const essayUsed = normalizeCreditsUsed(u.generateEssayCount)
      const autofillUsed = normalizeCreditsUsed(u.autoFillCount)

      const yearBucket = normalizeYear(u.yearOfStudent)
      const location = normalizeLocation(u)

      return {
        name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || "N/A",
        email: u.email || "N/A",
        registrationDate: createdAt ? createdAt.toLocaleString() : "N/A",
        createdAtMs,
        isPaid: u.statusEnum !== 0,
        accountProgress,
        essayUsed,
        autofillUsed,
        origin,
        yearBucket,
        location,
        school: u.schoolName || "N/A",
      }
    })

    const compare = (a, b) => {
      const dir = sortDir === "asc" ? 1 : -1
      const av = a[sortKey]
      const bv = b[sortKey]

      if (sortKey === "createdAt" || sortKey === "createdAtMs") {
        return dir * ((a.createdAtMs || 0) - (b.createdAtMs || 0))
      }

      if (typeof av === "number" && typeof bv === "number") return dir * (av - bv)
      return dir * String(av).localeCompare(String(bv))
    }

    const normalizedSortKey = sortKey === "createdAt" ? "createdAtMs" : sortKey
    return mapped.sort((a, b) => compare({ ...a, sortKey: normalizedSortKey }, { ...b, sortKey: normalizedSortKey }))
  }, [users, query, paidOnly, originFilter, sortKey, sortDir])

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("desc")
    }
  }

  return (
    <div>
      <div style={styles.filtersRow}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, year, school..."
          style={styles.searchInput}
        />
        <label style={styles.checkboxLabel}>
          <input type="checkbox" checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)} />
          Paid only
        </label>
        <select value={originFilter} onChange={(e) => setOriginFilter(e.target.value)} style={styles.select}>
          {originOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <Th onClick={() => toggleSort("name")} label="Name" />
              <Th onClick={() => toggleSort("email")} label="Email" />
              <Th onClick={() => toggleSort("createdAt")} label="Registered" />
              <Th onClick={() => toggleSort("isPaid")} label="Paid" />
              <Th onClick={() => toggleSort("accountProgress")} label="Progress" />
              <Th onClick={() => toggleSort("essayUsed")} label="Essay Used" />
              <Th onClick={() => toggleSort("autofillUsed")} label="Autofill Used" />
              <Th onClick={() => toggleSort("yearBucket")} label="Year" />
              <Th onClick={() => toggleSort("location")} label="Location" />
              <Th onClick={() => toggleSort("origin")} label="Origin" />
              <Th onClick={() => toggleSort("school")} label="School" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={styles.td}>{r.name}</td>
                <td style={styles.td}>{r.email}</td>
                <td style={styles.td}>{r.registrationDate}</td>
                <td style={styles.td}>{r.isPaid ? <Pill text="Paid" tone="green" /> : <Pill text="Free" tone="neutral" />}</td>
                <td style={styles.td}>
                  <ProgressBar value={r.accountProgress} />
                </td>
                <td style={styles.td}>{r.essayUsed}</td>
                <td style={styles.td}>{r.autofillUsed}</td>
                <td style={styles.td}>{r.yearBucket}</td>
                <td style={styles.td}>{r.location}</td>
                <td style={styles.td}>{r.origin}</td>
                <td style={styles.td}>{r.school}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.tableFooter}>
        Showing <b>{rows.length}</b> users
      </div>
    </div>
  )
}

const Th = ({ label, onClick }) => (
  <th onClick={onClick} style={styles.th} title="Click to sort">
    {label}
    <span style={styles.sortHint}>⇅</span>
  </th>
)

// -----------------------------
// Analytics tab
// -----------------------------
const PIE_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#a855f7", "#0ea5e9", "#f97316"]

const AnalyticsTab = ({ users }) => {
  const computed = useMemo(() => {
    const total = users.length

    const paidCount = users.filter((u) => u.statusEnum !== 0).length

    const signupsByDay = sortDesc(
      aggregateCounts(users, (u) => formatDateKey(u.createdAt))
    )
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))

    const yearDist = sortDesc(aggregateCounts(users, (u) => normalizeYear(u.yearOfStudent)))
    const originDist = sortDesc(aggregateCounts(users, (u) => normalizeOriginBucket(formatUserOrigin(u.userOrigin)))).slice(0, 8)
    const locationDist = sortDesc(aggregateCounts(users, (u) => normalizeLocation(u))).slice(0, 15)

    // Profile progress distribution
    const progressDist = sortDesc(
      aggregateCounts(users, (u) => bucketProgress(clamp(calculateAccountProgress(u), 0, 100)))
    ).sort((a, b) => {
      const order = ["0–20", "21–40", "41–60", "61–80", "81–99", "100", "Unknown"]
      return order.indexOf(a.name) - order.indexOf(b.name)
    })

    // GPA distribution (unweighted)
    const gpaDist = sortDesc(
      aggregateCounts(users, (u) => bucketGpa(parseGpa(u.academicUnweightedGPA)))
    ).sort((a, b) => {
      const order = ["<2.5", "2.5–3.0", "3.0–3.5", "3.5–4.0", "4.0+", "Unknown"]
      return order.indexOf(a.name) - order.indexOf(b.name)
    })

    // Activation funnel (milestones)
    const milestones = [
      { key: "signed_up", label: "Signed up", fn: () => true },
      { key: "profile_started", label: "Profile started (≥30%)", fn: (u) => calculateAccountProgress(u) >= 30 },
      { key: "profile_complete", label: "Profile complete (≥70%)", fn: (u) => calculateAccountProgress(u) >= 70 },
      { key: "added_schools", label: "Added schools", fn: (u) => Array.isArray(u.schoolsApplying) && u.schoolsApplying.length > 0 },
      { key: "added_activities", label: "Added activities", fn: (u) => Array.isArray(u.extracurricularActivities) && u.extracurricularActivities.length > 0 },
      { key: "used_essay", label: "Used essay generator", fn: (u) => normalizeCreditsUsed(u.generateEssayCount) > 0 },
      { key: "used_autofill", label: "Used autofill", fn: (u) => normalizeCreditsUsed(u.autoFillCount) > 0 },
      { key: "applied", label: "Applied to scholarship", fn: (u) => (Number(u.appliedScholarshipsCount) || 0) > 0 },
    ]

    const funnel = milestones.map((m) => ({
      name: m.label,
      value: users.filter((u) => m.fn(u)).length,
    }))

    // KPI-ish
    const profileComplete = users.filter((u) => calculateAccountProgress(u) >= 85).length
    const usedEssay = users.filter((u) => normalizeCreditsUsed(u.generateEssayCount) > 0).length
    const usedAutofill = users.filter((u) => normalizeCreditsUsed(u.autoFillCount) > 0).length
    const applied = users.filter((u) => (Number(u.appliedScholarshipsCount) || 0) > 0).length

    return {
      total,
      paidCount,
      paidRate: total ? Math.round((paidCount / total) * 100) : 0,
      profileComplete,
      usedEssay,
      usedAutofill,
      applied,
      signupsByDay,
      yearDist,
      originDist,
      locationDist,
      progressDist,
      gpaDist,
      funnel,
    }
  }, [users])

  return (
    <div>
      <div style={styles.kpiRow}>
        <StatCard label="Total users" value={computed.total} />
        <StatCard label="Paid users" value={computed.paidCount} hint={`${computed.paidRate}% paid`} />
        <StatCard label="Profile complete" value={computed.profileComplete} hint="≥85% progress" />
        <StatCard label="Used essay gen" value={computed.usedEssay} />
        <StatCard label="Used autofill" value={computed.usedAutofill} />
        <StatCard label="Applied" value={computed.applied} />
      </div>

      <div style={styles.grid2}>
        <Card title="Signups over time" subtitle="Daily signups (local time)">
          <ChartContainer>
            <ResponsiveContainer>
              <LineChart data={computed.signupsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        <Card title="Student stage distribution" subtitle="Normalized from yearOfStudent">
          <ChartContainer>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={computed.yearDist} dataKey="value" nameKey="name" outerRadius={110} label>
                  {computed.yearDist.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </div>

      <div style={styles.grid2}>
        <Card title="Acquisition / device mix" subtitle="Derived from userOrigin (rough)">
          <ChartContainer>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={computed.originDist} dataKey="value" nameKey="name" outerRadius={110} label>
                  {computed.originDist.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        <Card title="Top locations" subtitle="City, State (US) or Country (intl)">
          <ChartContainer>
            <ResponsiveContainer>
              <BarChart data={computed.locationDist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </div>

      <div style={styles.grid2}>
        <Card title="Activation funnel" subtitle="Where users drop off (computed milestones)">
          <ChartContainer>
            <ResponsiveContainer>
              <BarChart data={computed.funnel}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        <Card title="Profile completion distribution" subtitle="Account progress buckets">
          <ChartContainer>
            <ResponsiveContainer>
              <BarChart data={computed.progressDist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </div>

      <div style={styles.grid2}>
        <Card title="GPA distribution" subtitle="Unweighted GPA buckets">
          <ChartContainer>
            <ResponsiveContainer>
              <BarChart data={computed.gpaDist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        <Card title="Major interests (top 15)" subtitle="From majorsConsidering">
          <ChartContainer>
            <ResponsiveContainer>
              <BarChart
                data={sortDesc(
                  aggregateCounts(users, (u) => {
                    const arr = Array.isArray(u.majorsConsidering) ? u.majorsConsidering : []
                    // Count each user by their first selected major (simple default).
                    // If you want multi-count (all majors), tell me and I'll switch it.
                    return arr[0] ? String(arr[0]) : "Unknown"
                  })
                ).slice(0, 15)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </div>
    </div>
  )
}

// -----------------------------
// Main dashboard
// -----------------------------
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("analytics")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  const handleLogin = async () => {
    setIsLoading(true)
    setError("")
    try {
      const res = await fetch(
        `https://hive.avweb.org/users?username=${encodeURIComponent(username)}&password=${encodeURIComponent(
          password
        )}`
      )
      if (res.status === 200) {
        const json = await res.json()

        // IMPORTANT: Don't keep or log password fields client-side.
        // If API returns password, strip it to avoid accidental leaks.
        const sanitized = Array.isArray(json)
          ? json.map(({ password: _pw, ...rest }) => rest)
          : json

        setData(sanitized)
      } else {
        throw new Error("Invalid credentials")
      }
    } catch (e) {
      setError("Invalid credentials. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={styles.page}>
      {!data ? (
        <div style={styles.loginCard}>
          <h1 style={styles.h1}>Scholarhive Admin Dashboard</h1>
          <p style={styles.muted}>(internal use only)</p>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="username" style={styles.label}>
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              autoComplete="username"
            />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              autoComplete="current-password"
            />
          </div>

          {error ? <div style={styles.error}>{error}</div> : null}

          <button onClick={handleLogin} disabled={isLoading} style={styles.primaryBtn}>
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </div>
      ) : (
        <div style={styles.shell}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.h1}>Scholarhive Admin</h1>
              <div style={styles.muted}>
                {data.length} users • {activeTab === "analytics" ? "Analytics" : "Users"}
              </div>
            </div>

            <Tabs active={activeTab} onChange={setActiveTab} />
          </div>

          <div style={{ marginTop: 18 }}>
            {activeTab === "analytics" ? <AnalyticsTab users={data} /> : <UsersTable users={data} />}
          </div>
        </div>
      )}
    </div>
  )
}

// -----------------------------
// Styles
// -----------------------------
const styles = {
  page: {
    fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    backgroundColor: "#f3f4f6",
    minHeight: "100vh",
    padding: 20,
  },
  shell: {
    maxWidth: 1280,
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
  },
  h1: {
    color: "#111827",
    margin: 0,
    fontSize: 26,
    letterSpacing: "-0.3px",
  },
  muted: {
    color: "#6b7280",
    marginTop: 6,
    fontSize: 14,
  },
  loginCard: {
    width: "100%",
    maxWidth: 420,
    margin: "40px auto",
    padding: 22,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
    textAlign: "left",
  },
  label: {
    display: "block",
    marginBottom: 6,
    color: "#111827",
    fontSize: 13,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    outline: "none",
  },
  primaryBtn: {
    width: "100%",
    backgroundColor: "#2563eb",
    color: "white",
    padding: "10px 14px",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 700,
  },
  error: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    padding: "10px 12px",
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 14,
  },

  // Cards / layout
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 16,
  },
  card: {
    background: "#ffffff",
    borderRadius: 12,
    boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
    padding: 16,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
  },
  cardSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: "#6b7280",
  },
  statCard: {
    background: "#ffffff",
    borderRadius: 12,
    boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
    padding: 14,
  },
  statLabel: {
    fontSize: 12,
    color: "#6b7280",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  statValue: {
    marginTop: 6,
    fontSize: 22,
    fontWeight: 900,
    color: "#111827",
  },
  statHint: {
    marginTop: 6,
    fontSize: 12,
    color: "#6b7280",
  },

  // Tabs
  tabs: {
    display: "flex",
    gap: 8,
    background: "#ffffff",
    padding: 6,
    borderRadius: 12,
    boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
  },
  tabBtn: {
    border: "1px solid transparent",
    background: "transparent",
    padding: "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
    color: "#6b7280",
  },
  tabBtnActive: {
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  },

  // Users table
  filtersRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    outline: "none",
    fontSize: 14,
  },
  checkboxLabel: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    color: "#111827",
    fontSize: 14,
    background: "#fff",
    border: "1px solid #e5e7eb",
    padding: "8px 10px",
    borderRadius: 10,
  },
  select: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    outline: "none",
    fontSize: 14,
  },
  tableWrap: {
    overflowX: "auto",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 1200,
  },
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "#f9fafb",
  },
  th: {
    textAlign: "left",
    padding: "12px 12px",
    fontSize: 12,
    color: "#111827",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e5e7eb",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  sortHint: {
    marginLeft: 6,
    color: "#9ca3af",
    fontWeight: 900,
  },
  td: {
    padding: "12px 12px",
    borderBottom: "1px solid #f3f4f6",
    fontSize: 13,
    color: "#111827",
    verticalAlign: "middle",
  },
  trEven: { background: "#ffffff" },
  trOdd: { background: "#fcfcfd" },
  tableFooter: {
    marginTop: 10,
    color: "#6b7280",
    fontSize: 13,
  },

  // Pills / progress
  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  pillGreen: { background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" },
  pillRed: { background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" },
  pillBlue: { background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" },
  pillNeutral: { background: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" },

  progressOuter: {
    position: "relative",
    width: 170,
    height: 18,
    background: "#f3f4f6",
    borderRadius: 999,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  progressInner: {
    height: "100%",
    background: "#2563eb",
    borderRadius: 999,
  },
  progressText: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 900,
    color: "#111827",
  },
}